// ============================================================
// ChatMessage (v8.363.4) - one vox line in the stream.
//
// In character: the combatant's name in its livery with a --livery band;
// out of character: [the person]. An emote is italic; a roll shows
// `expr = total` with the dice on hover; a private vox is dimmed and
// says who it was to; a removed line says so and nothing else.
// ============================================================
import type { ReactNode } from 'react';
import type { ChatMessage as Msg, Person } from '../engine/chat';
import { conversationOf } from '../engine/chat';
import { pinTextOf } from '../engine/narrative/board';
import { liveryStyle } from '../utils/liveryStyle';
import type { Livery } from '../engine/roster/team';
import clsx from '../utils/clsx';
import styles from '../styles/ChatMessage.module.css';
import { useHandoutsContext } from '../contexts/HandoutsContext';
import useFileUrl from '../hooks/useFileUrl';
import { keptFileUrl } from '../utils/keptFiles';

/** The vox line for a reveal: the thumbnail, the title, a click opens it large (v8.363.10). */
function HandoutLine({ id, title }: { id: string; title: string }) {
  const ctx = useHandoutsContext();
  const h = ctx?.handouts.find((x) => x.id === id) ?? null;
  // Kept once per page (keptFileUrl): a renewed token was a new URL, and a new download, on every mount.
  const { url } = useFileUrl(h?.thumb ?? '', (ref) =>
    keptFileUrl(ref, (r) => ctx!.fileUrl({ ...h!, thumb: r }, 'thumb')),
  );
  if (!h) return <span data-testid="chat-handout">showed a handout: {title}</span>;
  return (
    <button
      type="button"
      className={styles.handout}
      onClick={() => ctx?.view(h.id)}
      data-testid="chat-handout"
      data-id={h.id}
    >
      {url ? <img src={url} alt="" className={styles.handoutThumb} /> : null}
      <span>
        showed <b>{h.title || h.kind}</b>
      </span>
    </button>
  );
}

interface Props {
  msg: Msg;
  me: string;
  people: Person[];
  onOpenDm?: (uid: string) => void;
  onRemove?: (id: string) => void;
  canRemove?: boolean;
  /** Pin this line to the board - the director's, and absent for everyone else. */
  onPin?: (line: string) => void;
}

/** The speaker's livery, when they speak in character. */
const liveryOf = (msg: Msg) => (msg.speakAs ? liveryStyle(msg.speakAs.livery as Livery | null) : undefined);

/** The speaker: their character in its livery, or [the person]. A click on someone else opens a private vox. */
function SenderName({ msg, mine, onOpenDm }: { msg: Msg; mine: boolean; onOpenDm?: (uid: string) => void }) {
  const name = msg.speakAs?.name || msg.fromName || '?';
  return (
    <button
      type="button"
      className={styles.name}
      style={liveryOf(msg)}
      onClick={() => !mine && onOpenDm?.(msg.fromUid)}
      title={msg.speakAs ? `${msg.speakAs.name} - ${msg.fromName}` : msg.fromName}
      data-testid="chat-name"
    >
      {msg.speakAs ? name : `[${name}]`}
    </button>
  );
}

function RollText({ msg }: { msg: Msg }) {
  return (
    <span className={styles.roll} title={msg.roll ? `dice: ${msg.roll.results.join(', ')}` : undefined}>
      🎲 {msg.roll?.expr ?? msg.text} = {msg.roll?.total ?? '?'}
    </span>
  );
}

/** The line's words, by kind: an emote, a handout shown, a roll, or plain speech. */
function MessageBody({ msg, sender }: { msg: Msg; sender: ReactNode }) {
  if (msg.kind === 'emote') {
    return (
      <span>
        * {msg.speakAs?.name || msg.fromName || '?'} {msg.text}
      </span>
    );
  }
  if (msg.kind === 'handout') {
    return (
      <span>
        {sender} <HandoutLine id={msg.handoutId} title={msg.text} />
      </span>
    );
  }
  if (msg.kind === 'roll') {
    return (
      <span>
        {sender} <RollText msg={msg} />
      </span>
    );
  }
  // The GM's announcement (v9.7.0): the words alone; the card around them says who speaks.
  if (msg.kind === 'system') return <span className={styles.announceText}>{msg.text}</span>;
  return (
    <span>
      {sender} {msg.text}
    </span>
  );
}

function RemoveButton({ onRemove }: { onRemove: () => void }) {
  return (
    <button type="button" className={styles.remove} aria-label="Remove message" title="Remove" onClick={onRemove}>
      ✕
    </button>
  );
}

/** Pin to the board: offered on a line of the story said to the table, never on a private vox. */
function PinButton({ msg, isDm, onPin }: { msg: Msg; isDm: boolean; onPin?: (line: string) => void }) {
  const line = onPin && !isDm ? pinTextOf(msg) : null;
  if (!line) return null;
  return (
    <button
      type="button"
      className={styles.pin}
      aria-label="Pin to the board"
      title="Pin to the board"
      onClick={() => onPin!(line)}
    >
      pin
    </button>
  );
}

export default function ChatMessage({ msg, me, people, onOpenDm, onRemove, canRemove = false, onPin }: Props) {
  const conv = conversationOf(msg, me);
  const isDm = conv.startsWith('dm:');
  const mine = msg.fromUid === me;
  const sender = <SenderName msg={msg} mine={mine} onOpenDm={onOpenDm} />;
  if (msg.kind === 'removed') {
    return (
      <div className={clsx(styles.line, styles.removed)} data-testid="chat-line" data-kind="removed">
        {sender} <span className={styles.removedText}>message removed</span>
      </div>
    );
  }
  // The GM's announcement is a card, not a line of talk (v9.7.0).
  if (msg.kind === 'system') {
    return (
      <div className={styles.announce} data-testid="chat-line" data-kind="system" data-conv={conv}>
        <div className={styles.announceHead}>
          <span className={styles.announceBy}>The GM</span>
          <PinButton msg={msg} isDm={false} onPin={onPin} />
          {canRemove && <RemoveButton onRemove={() => onRemove?.(msg.id)} />}
        </div>
        <MessageBody msg={msg} sender={sender} />
      </div>
    );
  }
  // A private vox says who it went to (mine) or that it was private (theirs).
  const other = isDm ? people.find((p) => p.uid === conv.slice(3)) : null;
  return (
    <div
      className={clsx(
        styles.line,
        msg.speakAs && styles.inCharacter,
        isDm && styles.whisper,
        msg.kind === 'emote' && styles.emote,
      )}
      style={liveryOf(msg)}
      data-testid="chat-line"
      data-kind={msg.kind}
      data-conv={conv}
    >
      {isDm && <span className={styles.whisperTag}>{mine ? `→ ${other?.name ?? 'someone'}` : 'private vox'}</span>}
      <MessageBody msg={msg} sender={sender} />
      <PinButton msg={msg} isDm={isDm} onPin={onPin} />
      {canRemove && <RemoveButton onRemove={() => onRemove?.(msg.id)} />}
    </div>
  );
}
